import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Row,
    Form,
    FormGroup,
    Label,
    Input,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import {
    blockActivateUser,
    getAllUsers,
    resetUser,
    getAllValuers,
    lookupUserByIdentifier,
    getAllRoles,
} from '../../services/ValitanApiService';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { enqueueSnackbar } from 'notistack';
import { ConfirmBlockDialog, ConfirmResetDialog } from './Common';
import Pagination from '../../components/Pagination/Pagination';
import { useSearch } from '../Users/UserSearch';
import { LoadingContext } from '../Utility/spinner/LoadingContext';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const BRAND_BLUE = "#0147C8";

const UsersListTables = () => {
    const { setIsLoading } = useContext(LoadingContext);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [error, setError] = useState(null);

    const [filters, setFilters] = useState({
        roleId: '',
        isValuer: '',
        sex: '',
        status: '', // active/banned
        nin: '',
        tin: '',
        certificateNumber: '',
    });

    const [paginationInfo, setPaginationInfo] = useState({
        pageNumber: 0,
        pageSize: 10,
        totalElements: 0,
        totalPages: 0,
    });

    const normalize = (u) => ({
        ...u,
        roles: (u.roles || []).map((r) => r.name),
        is_banned: u.is_banned === 'Yes',
    });

    // ✅ Fetch roles for filter dropdown
    const fetchRoles = useCallback(async () => {
        try {
            const { data } = await getAllRoles(0, 50);
            setRoles(data.content || []);
        } catch (err) {
            console.error('Error loading roles:', err);
            setRoles([]);
        }
    }, []);

    // ✅ Fetch users (paginated + filtered)
    const fetchUsers = useCallback(
        async (pageNumber = 0, pageSize = 10) => {
            try {
                setIsLoading(true);
                setError(null);

                let dataResponse;

                // If lookup filters present (NIN/TIN/Certificate)
                if (filters.nin || filters.tin || filters.certificateNumber) {
                    const { data } = await lookupUserByIdentifier({
                        nin: filters.nin || undefined,
                        tin: filters.tin || undefined,
                        certificate_number: filters.certificateNumber || undefined,
                    });
                    dataResponse = { content: [data], number: 0, size: 1, total_elements: 1, total_pages: 1 };
                }
                // If valuer filter applied
                else if (filters.isValuer) {
                    const { data } = await getAllValuers(filters.isValuer === 'true');
                    dataResponse = {
                        content: data,
                        number: 0,
                        size: data.length,
                        total_elements: data.length,
                        total_pages: 1,
                    };
                }
                // Default: all users (paginated)
                else {
                    const { data } = await getAllUsers(pageNumber, pageSize);
                    dataResponse = data;
                }

                setUsers((dataResponse.content || []).map(normalize));
                setPaginationInfo({
                    pageNumber: dataResponse.number,
                    pageSize: dataResponse.size,
                    totalElements: dataResponse.total_elements,
                    totalPages: dataResponse.total_pages,
                });
            } catch (err) {
                setError(err);
            } finally {
                setIsLoading(false);
            }
        },
        [filters, setIsLoading]
    );

    useEffect(() => {
        document.title = 'Users | Valitan';
    }, []);

    useEffect(() => {
        fetchUsers(0, paginationInfo.pageSize);
        fetchRoles();
    }, [fetchUsers, fetchRoles, paginationInfo.pageSize]);

    const { searchQuery, filteredItems, handleSearchChange } = useSearch(users);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleApplyFilters = () => {
        fetchUsers(0, paginationInfo.pageSize);
    };

    const handleResetFilters = () => {
        setFilters({
            roleId: '',
            isValuer: '',
            sex: '',
            status: '',
            nin: '',
            tin: '',
            certificateNumber: '',
        });
        fetchUsers(0, paginationInfo.pageSize);
    };

    const handlePreviousPage = () => {
        if (paginationInfo.pageNumber > 0) {
            fetchUsers(paginationInfo.pageNumber - 1, paginationInfo.pageSize);
        }
    };

    const handleNextPage = () => {
        if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
            fetchUsers(paginationInfo.pageNumber + 1, paginationInfo.pageSize);
        }
    };

    const handlePageChange = (pageNumber, pageSize) =>
        fetchUsers(pageNumber, pageSize);

    const handlePageSizeChange = (newPageSize) =>
        fetchUsers(0, newPageSize);

    const handleBlockUnblockUser = async (userId) => {
        try {
            setIsLoading(true);
            const response = await blockActivateUser(userId);

            if (response.status === 200) {
                const { message } = response.data;
                enqueueSnackbar(message, { variant: 'success' });
                await fetchUsers(paginationInfo.pageNumber, paginationInfo.pageSize);
            } else {
                enqueueSnackbar('Failed to update user status', { variant: 'error' });
            }
        } catch (err) {
            console.error('Error blocking/unblocking user:', err);
            enqueueSnackbar('An error occurred while updating', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleUserResetPassword = async (userId) => {
        try {
            setIsLoading(true);
            const response = await resetUser(userId);
            if (response.status === 200) {
                enqueueSnackbar('User password successfully reset', { variant: 'success' });
            } else {
                enqueueSnackbar('Failed to reset user password', { variant: 'error' });
            }
        } catch (err) {
            console.error('Error resetting password:', err);
            enqueueSnackbar('An error occurred while resetting password', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const rows = filteredItems.length ? filteredItems : [];

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Users management" breadcrumbItem="Users" />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <h4 className="card-title mb-0">
                                        <Link to="/add-user">
                                            <Button
                                                style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                                                className="add-btn float-end" id="create-btn">
                                                <i className="ri-add-line align-bottom me-1" /> Add
                                            </Button>
                                        </Link>
                                    </h4>
                                </CardHeader>

                                <CardBody>
                                    {/* 🔍 Filter Bar */}
                                    <Form className="mb-3">
                                        <Row className="g-3">
                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label for="roleId">Role</Label>
                                                    <Input
                                                        type="select"
                                                        name="roleId"
                                                        value={filters.roleId}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All Roles</option>
                                                        {roles.map((r) => (
                                                            <option key={r.id} value={r.id}>{r.name}</option>
                                                        ))}
                                                    </Input>
                                                </FormGroup>
                                            </Col>

                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="isValuer">Valuer</Label>
                                                    <Input
                                                        type="select"
                                                        name="isValuer"
                                                        value={filters.isValuer}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All</option>
                                                        <option value="true">Yes</option>
                                                        <option value="false">No</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>

                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="sex">Sex</Label>
                                                    <Input
                                                        type="select"
                                                        name="sex"
                                                        value={filters.sex}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All</option>
                                                        <option value="Male">Male</option>
                                                        <option value="Female">Female</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>

                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="status">Status</Label>
                                                    <Input
                                                        type="select"
                                                        name="status"
                                                        value={filters.status}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All</option>
                                                        <option value="active">Active</option>
                                                        <option value="banned">Banned</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>

                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label>NIN / TIN / Certificate</Label>
                                                    <Input
                                                        type="text"
                                                        placeholder="Enter NIN, TIN, or Cert. No"
                                                        value={filters.nin || filters.tin || filters.certificateNumber}
                                                        onChange={(e) =>
                                                            setFilters({
                                                                ...filters,
                                                                nin: e.target.value,
                                                                tin: e.target.value,
                                                                certificateNumber: e.target.value,
                                                            })
                                                        }
                                                    />
                                                </FormGroup>
                                            </Col>

                                            <Col md={12} className="d-flex justify-content-end">
                                                <Button
                                                    className="me-2"
                                                    onClick={handleApplyFilters}
                                                    style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                                                >
                                                    Apply
                                                </Button>
                                                <Button color="secondary" onClick={handleResetFilters}>
                                                    Reset
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Form>

                                    {/* Search box */}
                                    <Row className="g-4 mb-3">
                                        <Col className="col-sm">
                                            <div className="d-flex justify-content-sm-end">
                                                <div className="search-box ms-2">
                                                    <input
                                                        type="text"
                                                        className="form-control search"
                                                        placeholder="Search..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Table */}
                                    {error ? (
                                        <div>Error fetching users: {error.message}</div>
                                    ) : (
                                        <div className="table-responsive table-card mt-3 mb-1">
                                            <table className="table align-middle table-nowrap" id="userTable">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Name</th>
                                                        <th>Mobile</th>
                                                        <th>Email</th>
                                                        <th>Role</th>
                                                        <th>Sex</th>
                                                        <th>Status</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rows.length > 0 ? (
                                                        rows.map((user, idx) => (
                                                            <tr key={user.id}>
                                                                <td>{paginationInfo.pageNumber * paginationInfo.pageSize + idx + 1}</td>
                                                                <td>{user.first_name} {user.last_name}</td>
                                                                <td>{user.mobile_number}</td>
                                                                <td>{user.email}</td>
                                                                <td>
                                                                    {user.roles?.length ? (
                                                                        user.roles.map((name, i) => (
                                                                            <span key={i} className="badge me-1 text-white" style={{ backgroundColor: BRAND_BLUE }}>
                                                                                {name}
                                                                            </span>
                                                                        ))
                                                                    ) : (
                                                                        <span className="text-muted">No Role</span>
                                                                    )}
                                                                </td>
                                                                <td>{user.sex}</td>
                                                                <td>
                                                                    <span
                                                                        className={`badge ${user.is_banned ? 'bg-danger' : 'bg-success'}`}
                                                                    >
                                                                        {user.is_banned ? 'Banned' : 'Active'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div className="btn-group" role="group">
                                                                        <Link to={`/user-info/${user.id}`}
                                                                            className="btn btn-primary btn-sm"
                                                                            style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}>
                                                                            <i className="ri-eye-line" />
                                                                        </Link>
                                                                        <Link to={`/update-user/${user.id}`}
                                                                            className="btn btn-primary btn-sm"
                                                                            style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}>
                                                                            <i className="ri-edit-box-line" />
                                                                        </Link>
                                                                        <ConfirmResetDialog
                                                                            userId={user.id}
                                                                            onAccept={handleUserResetPassword}
                                                                        />
                                                                        <ConfirmBlockDialog
                                                                            userId={user.id}
                                                                            isBlocked={user.is_banned}
                                                                            onAccept={handleBlockUnblockUser}
                                                                        />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="8" className="text-center">No records found.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    <Pagination
                                        paginationInfo={paginationInfo}
                                        handleNextPage={handleNextPage}
                                        handlePreviousPage={handlePreviousPage}
                                        onPageChange={handlePageChange}
                                        onPageSizeChange={handlePageSizeChange}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
                <ConfirmDialog />
            </div>
        </React.Fragment>
    );
};

export default UsersListTables;
